/**
 * A plan-linked monthly line ↔ the deal's intake module — shared
 * client/server (S5b §3.9; moved here from `src/lib/service-plan-line.ts`
 * for R8, which re-exports it, so the acceptance trigger applies the SAME
 * rule as the builder).
 *
 * Install-time activation (`activateServicePlansForCustomer` →
 * `findPlanByDisplayName`) creates the customer's service from
 * `deal.serviceModules.<KEY>.intakeFields.servicePlan` — the INTAKE plan
 * name, not the quote line. Two writers keep that name in step with the plan
 * the deal bills:
 *   - the builder's plan switch (`writeDealQuoteWithCas`, client, S5b);
 *   - the acceptance of a proposal with options (`onProposalAccepted`,
 *     server, R8): the SIGNED option's plan — with a plan per option the
 *     builder's last switch may name another option's plan.
 *
 * Pure and isomorphic: no React, no Firebase, no app imports.
 */

/**
 * The `deal.serviceModules` key (a DealCategory) for a plan's `serviceType`
 * — the inverse of the activation service's `mapDealCategoryToServiceType`.
 * Null for plan types that have no intake module (OptiComm, 4G, Other).
 */
export function dealModuleKeyForPlanType(serviceType: string | undefined): string | null {
  const map: Record<string, string> = {
    NBN: 'NBN',
    Starlink: 'STARLINK',
    VoIP: 'VOIP',
    IT: 'IT',
    'Smart Home': 'SMART_HOME',
    Security: 'SECURITY',
    'WiFi Extension': 'WIFI_EXTENSION',
  };
  return (serviceType && map[serviceType]) || null;
}

/** S5b §3.9: the plan a switched monthly line now names (`serviceType` is the plan's). */
export interface IntakePlanName {
  serviceType: string;
  displayName: string;
}

const MODULE_KEY = /^[A-Za-z0-9_]+$/;

/** The deal shape these rules read: `serviceModules.<KEY>.intakeFields`. */
export type IntakeModules = { serviceModules?: Record<string, { intakeFields?: Record<string, unknown> } | undefined> } | undefined;

/**
 * The `deal.serviceModules` key whose intake `servicePlan` field a plan of
 * `serviceType` maps to — its DealCategory key, else the type itself — or
 * null when the deal has no such module / no string `servicePlan` field
 * there (nothing to keep in step). Keys are field-path safe by construction.
 */
export function intakeModuleKey(remote: IntakeModules, serviceType: string | undefined): string | null {
  const keys = [dealModuleKeyForPlanType(serviceType), serviceType];
  return (
    keys.find(
      (k): k is string => typeof k === 'string' && MODULE_KEY.test(k) && typeof remote?.serviceModules?.[k]?.intakeFields?.servicePlan === 'string',
    ) ?? null
  );
}

/**
 * S5b §3.9 — the field-path update that keeps the deal's intake plan name in
 * step with a plan line, or null when there is nothing to write. This is a
 * deliberate CROSS-DOMAIN write (see the module comment): a proposal switched
 * to another plan would otherwise bill the intake's old plan. The builder
 * applies it inside `writeDealQuoteWithCas`'s compare-and-set transaction
 * (`remote` is the transaction's read); the acceptance trigger applies it to
 * the deal it reads before the won-deal write. It only ever UPDATES an
 * existing intake field (module absent / no `servicePlan` field → no write)
 * and touches nothing else in the module (a dotted field path, not the module
 * object).
 */
export function intakePlanWrite(remote: IntakeModules, intake: IntakePlanName | undefined): Record<string, string> | null {
  if (!intake?.displayName?.trim()) return null;
  const key = intakeModuleKey(remote, intake.serviceType);
  if (!key) return null;
  if (remote?.serviceModules?.[key]?.intakeFields?.servicePlan === intake.displayName) return null;
  return { [`serviceModules.${key}.intakeFields.servicePlan`]: intake.displayName };
}
